import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      return;
    }

    setFormStatus('loading');

    const body = new URLSearchParams();
    body.append('name', (formData.get('name') as string || '').trim());
    body.append('email', (formData.get('email') as string || '').trim());
    body.append('subject', (formData.get('subject') as string || '').trim());
    body.append('message', (formData.get('message') as string || '').trim());

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qsek7nahb7382ovepg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') || serverMsg.includes('form data is spam')
          ? '提交失败，请稍后再试'
          : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料社区网',
    url: 'https://heiliao.shequ.cn/',
    description: '专注热门爆料、娱乐八卦与网红内容的社区聚合平台',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@heiliao.shequ.cn',
      availableLanguage: ['Chinese'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黑料社区网的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黑料社区网的内容来源于用户投稿、网络公开信息整理以及合作媒体供稿。我们拥有专业的编辑团队对内容进行审核和筛选，确保信息的真实性和时效性。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向黑料社区网投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系我们表单提交爆料信息，或直接发送邮件至 contact@heiliao.shequ.cn。我们承诺对爆料人信息严格保密。',
        },
      },
      {
        '@type': 'Question',
        name: '黑料社区网的内容是否经过核实？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的编辑团队会对收到的信息进行多方核实，确保内容的真实性。对于无法核实的内容，我们会标注"据网友爆料"等提示。如果发现不实信息，我们会在第一时间更正或删除。',
        },
      },
      {
        '@type': 'Question',
        name: '如果我是被爆料者，如何要求删除不实内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '如果您认为黑料社区网上的内容侵犯了您的合法权益，请通过联系我们表单或发送邮件至 contact@heiliao.shequ.cn 提出申诉。我们会在收到申诉后24小时内进行核实处理。',
        },
      },
    ],
  };

  const stats = [
    { value: '500万+', label: '月活跃用户', icon: 'ri-user-line' },
    { value: '10万+', label: '累计爆料', icon: 'ri-file-text-line' },
    { value: '99.7%', label: '内容真实率', icon: 'ri-shield-check-line' },
    { value: '24h', label: '内容更新频率', icon: 'ri-time-line' },
  ];

  const milestones = [
    { year: '2020', title: '平台创立', desc: '黑料社区网正式上线，专注娱乐八卦领域' },
    { year: '2021', title: '用户突破百万', desc: '凭借真实爆料和快速更新，月活用户突破100万' },
    { year: '2022', title: '内容扩展', desc: '新增网红黑料、社会热点等多个内容板块' },
    { year: '2023', title: '行业领先', desc: '成为中文互联网最大的爆料社区平台之一' },
    { year: '2024', title: '智能升级', desc: '引入AI辅助审核系统，进一步提升内容质量' },
    { year: '2025', title: '生态构建', desc: '建立完整的社区生态，包括投稿、互动、维权等体系' },
    { year: '2026', title: '全新改版', desc: '品牌全面升级，用户体验大幅优化' },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description="黑料社区网是专注热门爆料、娱乐八卦与网红内容的社区聚合平台。了解我们的品牌故事、发展历程与联系方式。"
        keywords="黑料社区网,关于我们,品牌介绍,联系方式,爆料社区"
        canonicalUrl="https://heiliao.shequ.cn/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero */}
          <section className="pt-24 pb-16 bg-background-900">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
              <div className="text-center max-w-3xl mx-auto mt-6">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading">
                  关于黑料社区网
                </h1>
                <p className="text-background-300 text-base md:text-lg leading-relaxed">
                  我们致力于打造中文互联网最真实、最及时的爆料内容社区。
                  在这里，每个吃瓜群众都能找到属于自己的那份"瓜"。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 bg-white border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                      <i className={`${stat.icon} text-primary-500 text-xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{stat.value}</div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">我们的故事</h2>
                <p className="mt-3 text-foreground-500 max-w-2xl mx-auto">
                  从一个小小的吃瓜群到百万用户的内容社区，黑料社区网走过了不平凡的七年
                </p>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="hidden md:block flex-1"></div>
                      <div className="absolute left-2 md:left-1/2 w-5 h-5 rounded-full bg-primary-500 border-4 border-white -translate-x-1/2 z-10"></div>
                      <div className="ml-10 md:ml-0 md:flex-1 bg-white rounded-lg border border-background-200 p-5">
                        <span className="text-xs font-bold text-primary-500">{item.year}</span>
                        <h3 className="text-lg font-bold text-foreground-800 mt-1 font-heading">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                      {index % 2 !== 0 && <div className="hidden md:block flex-1"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-14 md:py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">用户口碑</h2>
                <p className="mt-3 text-foreground-500">来自真实用户的评价</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { name: '吃瓜达人阿强', role: '资深用户', comment: '黑料社区网是我每天必刷的平台，更新速度快，内容真实可靠。好几次在这里看到的爆料后来都被证实了，真的牛！', rating: 5 },
                  { name: '娱乐观察家Linda', role: '自媒体作者', comment: '作为内容创作者，黑料社区网给我的选题提供了很多灵感。信息全面、分类清晰，比那些充斥着假消息的平台强太多了。', rating: 5 },
                  { name: '理性的瓜农', role: '3年老用户', comment: '从一个默默无闻的小社区发展到现在的规模，黑料社区网一直保持着对内容真实性的追求。希望能继续保持初心。', rating: 5 },
                  { name: '八卦研究员小王', role: '行业从业者', comment: '在娱乐行业工作多年，黑料社区网的爆料准确率在同类平台中是最高的。编辑团队的专业性值得肯定。', rating: 5 },
                  { name: '夜猫子小姐', role: '忠实粉丝', comment: '每天晚上睡前必刷黑料社区网，已经成了习惯。内容更新快，评论区也很有趣，像是一个大型吃瓜现场。', rating: 4 },
                  { name: '追剧小能手', role: '活跃用户', comment: '综艺剧透板块是我的最爱！很多内幕消息比官方发布还快，让我在朋友中永远是消息最灵通的那个。', rating: 5 },
                ].map((review, index) => (
                  <div key={index} className="bg-background-50 rounded-lg border border-background-200 p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{review.comment}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center text-white font-bold text-sm">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-800">{review.name}</div>
                        <div className="text-xs text-foreground-400">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">常见问题</h2>
                <p className="mt-3 text-foreground-500">关于黑料社区网，你可能想了解这些</p>
              </div>

              <div className="space-y-3">
                {[
                  { q: '黑料社区网的内容来源是什么？', a: '黑料社区网的内容来源于用户投稿、网络公开信息整理以及合作媒体供稿。我们拥有专业的编辑团队对内容进行审核和筛选，确保信息的真实性和时效性。' },
                  { q: '如何向黑料社区网投稿爆料？', a: '您可以通过本页面的联系我们表单提交爆料信息，或直接发送邮件至 contact@heiliao.shequ.cn。我们承诺对爆料人信息严格保密。' },
                  { q: '黑料社区网的内容是否经过核实？', a: '我们的编辑团队会对收到的信息进行多方核实，确保内容的真实性。对于无法核实的内容，我们会标注"据网友爆料"等提示。如果发现不实信息，我们会在第一时间更正或删除。' },
                  { q: '如果我是被爆料者，如何要求删除不实内容？', a: '如果您认为黑料社区网上的内容侵犯了您的合法权益，请通过联系我们表单或发送邮件至 contact@heiliao.shequ.cn 提出申诉。我们会在收到申诉后24小时内进行核实处理。' },
                ].map((faq, index) => (
                  <details key={index} className="group bg-white rounded-lg border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors duration-200">
                      <span className="text-sm font-semibold text-foreground-800 pr-4 font-heading">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="py-14 md:py-20 bg-white">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">联系我们</h2>
                <p className="mt-3 text-foreground-500">
                  有爆料线索？有合作意向？或是对我们的内容有疑问？欢迎随时联系我们。
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 space-y-5"
              >
                <div className="honeypot-field">
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-sm transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-sm transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-sm transition-colors duration-200 cursor-pointer"
                  >
                    <option value="">请选择主题</option>
                    <option value="投稿爆料">投稿爆料</option>
                    <option value="内容申诉">内容申诉</option>
                    <option value="商务合作">商务合作</option>
                    <option value="意见建议">意见建议</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-sm transition-colors duration-200 resize-none"
                    placeholder="请详细描述您的需求或问题（限500字）"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 whitespace-nowrap cursor-pointer text-sm"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送消息'}
                </button>

                {(formStatus === 'success' || formStatus === 'error') && (
                  <div className={`text-center text-sm p-3 rounded-lg ${
                    formStatus === 'success'
                      ? 'bg-accent-50 text-accent-700'
                      : 'bg-primary-50 text-primary-700'
                  }`}>
                    {formMessage}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}